/**
 * LevelUpVFXHandler — the level-up beat, drawn on the leveling character's
 * body for everyone who can see that character (lane AP, 2026-10-08).
 *
 * Two inputs, one funnel:
 *  - EVENTS.PLAYER_LEVEL_UP — the server's `levelUp` message, sent ONLY to the
 *    leveling client (and the offline ProgressionSystem). Plays on the local
 *    player.
 *  - EVENTS.NET_MESSAGE_PLAYER_LEVELED_UP — the server's room-wide structured
 *    `playerLeveledUp { sessionId, level }`. Plays on that player when they
 *    are ANOTHER player on this map; the leveler's own copy is skipped (its
 *    PLAYER_LEVEL_UP already played). The "has reached Level N" chat line is
 *    text only — parsing it let any rewording break the effect, and any player
 *    typing that sentence spoof one.
 *
 * Both play the `level_up` def at the character's sprite centre and follow
 * the character while the burst lives. Nobody gets a screen flash, hit-stop or
 * slow-mo: the old path flashed the LEVELER's screen (triggerLevelUp) and, via
 * `teleport_flash`'s def-level screenFlash/screenShake, every OTHER player's
 * screen whenever anyone levelled (DJ, stream notes row 36).
 */
import { EVENTS } from '../../../shared/EventBus';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { entityVfxAnchor } from '../players/entityVfxAnchor';
import { localSessionId } from './entityPositions';

/** The one effect id a level-up plays, local or remote. */
export const LEVEL_UP_EFFECT_ID = 'level_up';

export interface LevelUpVFXDeps {
  playById: (effectId: string, x: number, y: number, overrides?: Record<string, unknown>) => void;
}

interface PositionedEntity {
  id?: string;
  position?: { x: number; y: number };
  width?: number;
  height?: number;
}

export class LevelUpVFXHandler {
  constructor(private deps: LevelUpVFXDeps) {}

  register(sub: (event: string, handler: (data: any) => void) => void): void {
    sub(EVENTS.PLAYER_LEVEL_UP, (data) => this.onLocalLevelUp(data));
    sub(EVENTS.NET_MESSAGE_PLAYER_LEVELED_UP, (data) => this.onPlayerLeveledUp(data));
  }

  private onLocalLevelUp(data: { level?: number; newLevel?: number } | undefined): void {
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    const player = entitySystem?.getPlayerManager?.()?.getPlayer?.() as PositionedEntity | undefined;
    if (!player) return;
    this.playOn(player, player.id ?? '', data?.level ?? data?.newLevel);
  }

  private onPlayerLeveledUp(message: { sessionId?: unknown; level?: unknown } | undefined): void {
    const sessionId = message?.sessionId;
    const level = message?.level;
    if (typeof sessionId !== 'string' || !sessionId) return;
    if (typeof level !== 'number' || !Number.isFinite(level)) return;
    if (sessionId === localSessionId()) return;

    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    const remote: PositionedEntity | undefined = entitySystem
      ?.getRemotePlayerManager?.()
      ?.getOtherPlayers?.()
      ?.get?.(sessionId);
    if (remote) this.playOn(remote, sessionId, level);
  }

  private playOn(entity: PositionedEntity, followId: string, level: number | undefined): void {
    const anchor = entityVfxAnchor(entity);
    if (!anchor) return;
    this.deps.playById(LEVEL_UP_EFFECT_ID, anchor.x, anchor.y, {
      newLevel: level ?? '',
      targetId: followId || undefined,
    });
  }
}
